import { useCallback, useEffect, useRef, useState } from "react";
import { adminService } from "../../features/admin/service";
import type { SessionConversationMessage, SessionConversationOutput, SessionConversationResponse } from "../../features/admin/types";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { formatISTDateTime } from "../../utils/dateUtils";
import Spinner from "../ui/Spinner";

const PAGE_SIZE = 30;

const FEATURE_LABELS: Record<string, string> = {
  flyer: "Flyer",
  itinerary: "Itinerary",
  video: "Video",
  deal: "Ask Deal",
  market_research: "Market Research",
  dm: "DM",
  enquiry: "Enquiry",
};

/** One page of a conversation. `thread_id` only matters for enquiry chats that opened several threads. */
export type ConversationPageFetcher = (params: {
  cursor?: string | null;
  limit: number;
  thread_id?: string;
  /** Why the admin is reading this; the server needs it for session / DM views and audits it. */
  reason?: string;
}) => Promise<SessionConversationResponse>;

const MIN_REASON_LENGTH = 5;

function OutputView({ output }: { output: SessionConversationOutput }) {
  const label = output.label || output.kind.toUpperCase();
  if (output.kind === "image") {
    return (
      <a href={output.url} target="_blank" rel="noreferrer" className="block" title={label}>
        <img src={output.url} alt={label} loading="lazy" className="max-h-48 rounded-lg border border-gray-700 object-contain bg-gray-950" />
      </a>
    );
  }
  if (output.kind === "video") {
    return <video src={output.url} controls preload="metadata" className="max-h-56 w-full rounded-lg border border-gray-700 bg-black" />;
  }
  return (
    <a
      href={output.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-800 text-indigo-300 hover:text-white hover:bg-gray-700 text-xs"
    >
      Open {label}
    </a>
  );
}

function MessageBubble({ message }: { message: SessionConversationMessage }) {
  // DM / enquiry chats say which side is "self"; session chats are user vs assistant.
  const isUser = message.is_self ?? message.role === "user";
  return (
    <div data-testid="session-message" data-role={message.role} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-xl px-3 py-2 ${isUser ? "bg-indigo-600/20 border border-indigo-500/30" : "bg-gray-800/80 border border-gray-700/60"}`}>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`text-[10px] uppercase tracking-wide font-semibold ${isUser ? "text-indigo-300" : "text-gray-400"}`}>{message.role}</span>
          {message.sender_name && <span className="text-[11px] text-gray-300">{message.sender_name}</span>}
          {message.ai_reply && (
            <span data-testid="ai-reply-badge" className="px-1.5 py-0.5 bg-emerald-900/50 text-emerald-300 rounded text-[10px] font-medium">
              AI reply
            </span>
          )}
          {message.type && message.type !== "text" && (
            <span className="px-1.5 py-0.5 bg-gray-900 text-gray-400 rounded text-[10px]">{message.type}</span>
          )}
          {message.created_at && <span className="text-[10px] text-gray-500">{formatISTDateTime(message.created_at)}</span>}
        </div>
        {message.text && <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">{message.text}</p>}
        {message.destination && <p className="text-xs text-gray-500 mt-1">Destination: {message.destination}</p>}
        {message.attachments.length > 0 && (
          <ul className="mt-2 space-y-1">
            {message.attachments.map((a, i) => (
              <li key={i} className="text-xs text-gray-400">
                📎{" "}
                {a.url ? (
                  <a href={a.url} target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-white underline">{a.name}</a>
                ) : (
                  <span>{a.name}</span>
                )}
                {a.status && <span className="ml-1 text-gray-500">({a.status})</span>}
                {a.summary && <span className="block text-gray-500 pl-4">{a.summary}</span>}
              </li>
            ))}
          </ul>
        )}
        {message.deals && message.deals.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {message.deals.map((d, i) => (
              <li key={d.id || i} className="text-xs text-gray-400">Deal: {d.title || d.id}</li>
            ))}
          </ul>
        )}
        {message.outputs.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.outputs.map((o, i) => <OutputView key={`${o.url}-${i}`} output={o} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Right-side, read-only transcript of one conversation. By default a user chat
 * session (flyer, itinerary, video, Ask Deal, market research); pass `fetchPage`
 * to show another conversation in the same shape (DM thread, enquiry chat).
 * Newest messages last; "Load older messages" pages backwards. Mount it only
 * while open (and with a `key` per conversation).
 */
export default function SessionConversationDrawer({
  userId,
  sessionId,
  title,
  fetchPage,
  requireReason = false,
  onClose,
}: {
  userId: string;
  sessionId: string;
  title?: string;
  fetchPage?: ConversationPageFetcher;
  /** Ask for an audited reason before anything is loaded (user session and DM views). */
  requireReason?: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<string | null>(requireReason ? null : "");
  const [reasonDraft, setReasonDraft] = useState("");
  const [data, setData] = useState<SessionConversationResponse | null>(null);
  const [messages, setMessages] = useState<SessionConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const requestId = useRef(0);
  // Read through a ref so an inline fetcher from the parent never re-triggers loading.
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  useBodyScrollLock();

  const getPage = useCallback(
    (params: { cursor?: string | null; limit: number; thread_id?: string }) => {
      const withReason = reason ? { ...params, reason } : params;
      return fetchRef.current
        ? fetchRef.current(withReason)
        : adminService.getUserSessionConversation(
            userId,
            sessionId,
            withReason.cursor ? { cursor: withReason.cursor, limit: withReason.limit, ...(reason ? { reason } : {}) } : { limit: withReason.limit, ...(reason ? { reason } : {}) },
          );
    },
    [userId, sessionId, reason],
  );

  const load = useCallback(() => {
    if (reason === null) {
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setData(null);
    setMessages([]);
    getPage(threadId ? { limit: PAGE_SIZE, thread_id: threadId } : { limit: PAGE_SIZE })
      .then((res) => {
        if (id !== requestId.current) return;
        setData(res);
        setMessages(res.messages);
      })
      .catch((e: any) => {
        if (id !== requestId.current) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load conversation");
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [getPage, threadId, reason]);

  useEffect(() => {
    load();
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadOlder = () => {
    if (!data?.next_cursor || loadingOlder) return;
    const id = requestId.current;
    setLoadingOlder(true);
    setError(null);
    getPage({ cursor: data.next_cursor, limit: PAGE_SIZE, ...(threadId ? { thread_id: threadId } : {}) })
      .then((res) => {
        if (id !== requestId.current) return;
        setData(res);
        setMessages((prev) => [...res.messages, ...prev]);
      })
      .catch((e: any) => {
        if (id !== requestId.current) return;
        setError(e?.response?.data?.detail || e?.message || "Failed to load older messages");
      })
      .finally(() => {
        if (id === requestId.current) setLoadingOlder(false);
      });
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Session conversation"
        className="h-full w-full max-w-xl bg-gray-900 border-l border-gray-800 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-800 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-white truncate">{title || "Session Conversation"}</h2>
            <p className="text-[10px] text-gray-500 font-mono truncate">{data?.session_id || sessionId}</p>
            {data?.thread && (
              <p className="text-xs text-gray-300 mt-0.5 truncate" data-testid="conversation-parties">
                {data.thread.buyer_name || data.thread.buyer_id} (buyer) ↔ {data.thread.seller_name || data.thread.seller_id} (seller)
                {data.thread.deal_title ? ` · ${data.thread.deal_title}` : ""}
              </p>
            )}
            {data && (
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="px-1.5 py-0.5 bg-indigo-900/60 text-indigo-300 rounded text-[10px]">{FEATURE_LABELS[data.feature] || data.feature}</span>
                {data.source === "archived" && <span className="px-1.5 py-0.5 bg-amber-900/50 text-amber-300 rounded text-[10px]">archived</span>}
                {data.thread?.ai_reply_active && <span className="px-1.5 py-0.5 bg-emerald-900/50 text-emerald-300 rounded text-[10px]">AI auto-reply on</span>}
                {data.enquiry?.status && <span className="px-1.5 py-0.5 bg-gray-800 text-gray-300 rounded text-[10px]">{data.enquiry.status}</span>}
                <span className="text-[10px] text-gray-500">{data.total} message(s) · read-only</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-lg font-bold"
          >
            ×
          </button>
        </div>

        {data?.threads && data.threads.length > 1 && (
          <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b border-gray-800 shrink-0" role="group" aria-label="Seller threads">
            {data.threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setThreadId(t.id)}
                disabled={loading}
                className={`px-2 py-1 rounded-md text-[11px] ${t.id === data.session_id ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-300 hover:text-white"}`}
              >
                {t.seller_name || t.seller_id}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {data?.enquiry?.pre_chat_transcript && (
            <details className="rounded-lg border border-gray-800 bg-gray-950/60 p-2 text-xs text-gray-400">
              <summary className="cursor-pointer text-gray-300">Get Details AI chat before this thread (buyer and AI)</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words text-gray-400">{data.enquiry.pre_chat_transcript}</pre>
            </details>
          )}
          {reason === null ? (
            <form
              className="py-10 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (reasonDraft.trim().length >= MIN_REASON_LENGTH) setReason(reasonDraft.trim());
              }}
            >
              <p className="text-sm text-gray-300">This conversation is private. State why you are reading it; the view is logged against your account.</p>
              <label className="block text-xs text-gray-400" htmlFor="conversation-reason">Reason for viewing</label>
              <textarea
                id="conversation-reason"
                value={reasonDraft}
                onChange={(e) => setReasonDraft(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="e.g. support ticket #1234 — user reports a wrong flyer"
                className="w-full rounded-lg bg-gray-950 border border-gray-700 px-3 py-2 text-sm text-white"
              />
              <button
                type="submit"
                disabled={reasonDraft.trim().length < MIN_REASON_LENGTH}
                className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-40"
              >
                View conversation
              </button>
            </form>
          ) : loading ? (
            <div className="py-16 text-center text-gray-500"><Spinner /></div>
          ) : error && messages.length === 0 ? (
            <div className="py-16 text-center" role="alert">
              <p className="text-sm text-red-400">{error}</p>
              <button onClick={load} className="mt-3 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 hover:text-white text-xs">Retry</button>
            </div>
          ) : messages.length === 0 ? (
            <div className="py-16 text-center text-gray-500">{data?.thread ? "No messages in this conversation." : "No messages in this session."}</div>
          ) : (
            <>
              {data?.next_cursor && (
                <div className="text-center">
                  <button
                    onClick={loadOlder}
                    disabled={loadingOlder}
                    className="px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 hover:text-white text-xs disabled:opacity-50"
                  >
                    {loadingOlder ? "Loading…" : "Load older messages"}
                  </button>
                </div>
              )}
              {error && <p className="text-center text-xs text-red-400" role="alert">{error}</p>}
              {messages.map((m, i) => <MessageBubble key={i} message={m} />)}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
